import { flow } from "mobx";
import { observer, Observer } from "mobx-react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { QuestionForm } from "../../../api/questionForm/QuestionFormTypes";
import { CvpUpdateSubmittedAnonymousResponse } from "../../../api/questionForm/updateSubmittedAnonymous";
import { useLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { unsavedChanges } from "../../../WSUtils/unsavedChanges";
import { invalidateQueries } from "../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../WSUtils/UserInfoContext";
import { awaitableWhen } from "../../../WSUtils/util";
import { CVEditContext } from "../CVEditContext";
import { CVEQuestionStepContext } from "./CVEQuestionStepContext";
import { CVEQuestionStepContextStore } from "./CVEQuestionStepContextStore";
import { getQuestionFormToken } from "./getQuestionFormToken";
import { CVEQuestion } from "./QuestionComponents/CVEQuestion";
import { validateQuestion } from "./QuestionType";
import { useQuestionFormAnswers } from "./useQuestionFormAnswers";
import { useQuestionFormLog } from "./useQuestionFormLog";

export interface CVEQuestionFormProps {
  item: QuestionForm;
  header: any;
  description: any;
}

export const CVEQuestionForm = observer(
  ({ item, header, description }: CVEQuestionFormProps) => {
    const { questions, id } = item;
    const L = useLocStore(LocContext.CV);
    const userInfo = useContext(UserInfoContext);
    const cvEditContext = useContext(CVEditContext);
    const queryClient = useQueryClient();

    const questionFormAPI = userInfo.fullAPI.questionFormAPI;

    const { qfl, isDone: doneFetchingQfl } = useQuestionFormLog(item.id);
    const { answers } = useQuestionFormAnswers(qfl?.id);

    const token = getQuestionFormToken();

    useEffect(() => {
      if (token) return;
      const mustBeInitialized = doneFetchingQfl && !qfl;
      if (!mustBeInitialized) return;

      const initialize = flow(function* () {
        const { irel, trel } = cvEditContext?.applyingForJob ?? {};
        const result = yield questionFormAPI.initiateQuestionForm({
          questionFormId: id,
          relatedType: trel,
          relatedId: irel,
          userInfo: userInfo,
          showSentryDialogOnError: true,
        });

        if (result.ok) {
          invalidateQueries(queryClient, [
            "questionFormsAPI.getQuestionFormLogs",
            id,
          ]);
        }
      });

      initialize();
    }, [doneFetchingQfl, qfl]);

    const cveQuestionStepContextStore = useMemo(
      () => new CVEQuestionStepContextStore(),
      []
    );

    const submit = flow(function* () {
      const result: CvpUpdateSubmittedAnonymousResponse =
        yield questionFormAPI.updateSubmittedAnonymous({
          token: getQuestionFormToken(),
          showSentryDialogOnError: true,
          userInfo,
        });

      if (result.ok && result.item) {
        window.location.href = result.item;
        return;
      }

      yield invalidateQueries(queryClient, [
        "questionFormsAPI.getQuestionFormLogs",
        id,
      ]);
    });

    const [waiting, setWaiting] = useState(false);

    const onSubmit = flow(function* () {
      setWaiting(true);

      try {
        yield awaitableWhen(() => unsavedChanges.get().length === 0);
        cveQuestionStepContextStore.hasClickedSubmit = true;

        const notOk = questions.find((question) => {
          const validation = validateQuestion(question, answers.items);
          return !validation.ok;
        });

        if (!notOk) {
          yield submit();
        }
      } finally {
        setWaiting(false);
      }
    });

    return (
      <CVEQuestionStepContext.Provider value={cveQuestionStepContextStore}>
        <QuestionFormStyles className="cve-questions-segment cve-question-form">
          <div className="cvp-h2 cve-question-form-header cve-header">
            {header}
          </div>
          <p className="cve-question-form-description">{description}</p>
          <Observer
            render={() => {
              const form = questions.map((question) => (
                <CVEQuestion key={question.id} question={question} />
              ));

              return <>{form}</>;
            }}
          />

          <div className="cve-question-form-buttons">
            <Observer
              render={() => {
                const notOk = questions.find((question) => {
                  const validation = validateQuestion(
                    question,
                    answers?.items ?? []
                  );
                  return !validation.ok;
                });

                const showValidationErrorMessage =
                  notOk && cveQuestionStepContextStore.hasClickedSubmit;

                if (!showValidationErrorMessage) return null;
                return (
                  <Message
                    className="cve-question-validation-message p-message p-message-error"
                    severity="error"
                    text={L.id("c96019e0d292").loc("Noen svar må korrigeres")}
                  />
                );
              }}
            />

            <Button
              className="cve-question-submit-button"
              label={L.id("a531a9b81f7a").loc("Lever inn")}
              icon="pi pi-sign-in"
              loading={waiting}
              onClick={onSubmit}
            />
          </div>
        </QuestionFormStyles>
      </CVEQuestionStepContext.Provider>
    );
  }
);

export const QuestionFormStyles = styled.div`
  &.cve-question-form {
    .cve-question-validation-message {
      display: block;
      margin-bottom: 0.5rem;
    }

    .cve-question-form-buttons {
      margin-top: 3rem;
    }

    .cve-question-form-header {
    }

    .cve-question-form-description {
      margin-bottom: 2rem;
    }

    .cve-question-submit-button {
      width: 100%;
    }
  }
`;
